import { jsx, jsxs } from "react/jsx-runtime";
import { Fade, useTheme, useThemeProps } from "@mui/material";
import { Children, forwardRef } from "react";
import { useSlotProps } from "../../base/index.js";
import { MoreHoriz } from "../../icons/index.js";
import { useItemOnHover } from "../../hooks/index.js";
import { ActionBar } from "../ActionBar/ActionBar.js";
import { Actions } from "./components/Actions.js";
import { Body } from "./components/Body.js";
import { Content } from "./components/Content.js";
import { Heading } from "./components/Heading.js";
import { Icon } from "./components/Icon.js";
import { Root } from "./components/Root.js";
import { cardName } from "./classes.js";
import { useCardState } from "./contexts/cardState.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
const Card = forwardRef(function Card2(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: cardName
  });
  const cardState = useCardState();
  const {
    actionBarProps,
    actionBarItems = [],
    active,
    children,
    components,
    componentsProps,
    contentPaddingX = 2,
    contentPaddingY = 1.5,
    hoverStateBorderWidth = 3,
    icon,
    size,
    sx,
    testId = `${cardName}-Root`,
    width,
    ...rest
  } = props;
  const {
    onEnter: onEnterActions,
    onLeave: onLeaveActions,
    visible: actionBarVisible
  } = useItemOnHover(cardState.active);
  const utilityClasses = useUtilityClasses(props);
  const { spacing } = useTheme();
  const rootSlotProps = useSlotProps({
    className: utilityClasses.root,
    elementType: Root,
    externalForwardedProps: rest,
    externalSlotProps: componentsProps?.root,
    ownerState: props,
    additionalProps: {
      ref,
      width,
      "data-testid": testId,
      as: components?.Root,
      onMouseEnter: onEnterActions,
      onMouseLeave: onLeaveActions,
      sx: {
        ...sx,
        "--card-content-padding-x": spacing(contentPaddingX),
        "--card-content-padding-y": spacing(contentPaddingY),
        "--card-hover-state-border-width": `${hoverStateBorderWidth}px`,
        "--card-border-radius": spacing(1)
      }
    }
  });
  const contentSlotProps = useSlotProps({
    className: utilityClasses.content,
    elementType: Content,
    externalSlotProps: componentsProps?.content,
    ownerState: props,
    additionalProps: {
      as: components?.Content,
      gap: 0.25
    }
  });
  const iconSlotProps = useSlotProps({
    className: utilityClasses.icon,
    elementType: Icon,
    externalSlotProps: componentsProps?.icon,
    ownerState: props,
    additionalProps: {
      as: components?.Icon
    }
  });
  const headingSlotProps = useSlotProps({
    className: utilityClasses.heading,
    elementType: Heading,
    externalSlotProps: componentsProps?.heading,
    ownerState: props,
    additionalProps: {
      as: components?.Heading,
      direction: "row"
    }
  });
  const actionsSlotProps = useSlotProps({
    className: utilityClasses.actions,
    elementType: Actions,
    externalSlotProps: componentsProps?.actions,
    ownerState: props,
    additionalProps: {
      as: components?.Actions,
      direction: "row"
    }
  });
  const bodySlotProps = useSlotProps({
    className: utilityClasses.body,
    elementType: Body,
    externalSlotProps: componentsProps?.body,
    ownerState: props,
    additionalProps: {
      as: components?.Body,
      gap: 1
    }
  });
  const [titleChild, ...bodyChildren] = Children.toArray(children);
  const showBody = bodyChildren.length > 0;
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, "data-tidal": "Card", children: [
    icon && /* @__PURE__ */ jsx(Icon, { ...iconSlotProps, children: icon }),
    /* @__PURE__ */ jsxs(Content, { ...contentSlotProps, children: [
      /* @__PURE__ */ jsxs(Heading, { ...headingSlotProps, children: [
        titleChild,
        actionBarItems.length > 0 && /* @__PURE__ */ jsx(Actions, { ...actionsSlotProps, onMouseLeave: onEnterActions, children: /* @__PURE__ */ jsx(Fade, { in: actionBarVisible, children: /* @__PURE__ */ jsx(
          ActionBar,
          {
            ...actionBarProps,
            limit: 0,
            menuIcon: /* @__PURE__ */ jsx(MoreHoriz, { fontSize: "inherit" }),
            size: cardState.size,
            children: actionBarItems
          }
        ) }) })
      ] }),
      showBody && /* @__PURE__ */ jsx(Body, { ...bodySlotProps, children: bodyChildren })
    ] })
  ] });
});
export {
  Card
};
//# sourceMappingURL=Card.js.map
